<template>
  <div>
    <p v-if="isLoading" class="text-subtle p-4">Hämtar ansökan...</p>
    <p v-else-if="error" class="error-text p-4">{{ error }}</p>

    <template v-else-if="application">
      <AppCard v-if="cancelReasons.length" class="mb-6 application-cancelled-card">
        <div>
          <p class="font-semibold">Ansökan har makulerats</p>
          <ul class="mt-1 list-disc">
            <li v-for="reason in cancelReasons" :key="reason">{{ reason }}</li>
          </ul>
        </div>
      </AppCard>

      <AppCard v-if="rejectReasons.length" class="mb-6 application-cancelled-card">
        <div>
          <p class="font-semibold">Ansökan har nekats</p>
          <ul class="mt-1 list-disc">
            <li v-for="reason in rejectReasons" :key="reason">{{ reason }}</li>
          </ul>
        </div>
      </AppCard>

      <AppCard v-if="application.comment" class="mb-6">
        <h3 class="mb-1">Kommentar från förmedlare</h3>
        <p class="broker-comment">{{ application.comment }}</p>
      </AppCard>

      <!-- Two-column layout: nav + sections -->
      <div class="grid grid-cols-1 gap-6 md:grid-cols-[200px_minmax(0,1fr)]">
        <SectionNav :active-section="activeSection" :sections="navigationSections" @navigate="lock" />

        <div class="min-w-0 flex flex-col gap-6">
          <LoanAgreementsSection v-if="showLoanAgreementsSection" :section="applicationSections.loanAgreements" />
          <DepositLoanAgreementsSection
            v-if="showDepositLoanAgreementsSection"
            :section="applicationSections.depositLoanAgreements"
          />
          <LoanSection :section="applicationSections.loan" />
          <CashContributionSection v-if="showCashContributionSection" :section="applicationSections.cashContribution" />

          <!-- Återbetalningsförmåga -->
          <RepaymentCapacitySection :section="applicationSections.repaymentCapacity" />

          <!-- Bostaden -->
          <PropertySection :section="applicationSections.property" />

          <!-- Pantbrev -->
          <MortgageDeedsSection v-if="showMortgageDeedsSection" :section="applicationSections.mortgageDeeds" />

          <!-- Utbetalning -->
          <PayoutSection :section="applicationSections.payout" />

          <!-- Lånedelar -->
          <LoanPartsSection :section="applicationSections.loanParts" />
        </div>
      </div>
    </template>
  </div>
</template>

<script setup lang="ts">
import { computed, nextTick, onBeforeUnmount, onMounted, watch } from 'vue'

import { useActiveSection } from '@/composables/useActiveSection'
import { useMetadata } from '@/composables/useMetadata'
import { isRealEstateCollateral } from '@/utils/applicationDisplay'

import { useApplicationContext } from '../application.context'
import SectionNav from './components/SectionNav.vue'
import LoanSection from './components/sections/LoanSection.vue'
import RepaymentCapacitySection from './components/sections/RepaymentCapacitySection.vue'
import CashContributionSection from './components/sections/CashContributionSection.vue'
import PropertySection from './components/sections/PropertySection.vue'
import PayoutSection from './components/sections/PayoutSection.vue'
import MortgageDeedsSection from './components/sections/MortgageDeedsSection.vue'
import LoanPartsSection from './components/sections/LoanPartsSection.vue'
import LoanAgreementsSection from './components/sections/LoanAgreementsSection.vue'
import DepositLoanAgreementsSection from './components/sections/DepositLoanAgreementsSection.vue'
import { applicationSections, type NavigationSection } from './components/sections/sections'
import { AppCard } from '@staff/ui'

const { application, isLoading, error } = useApplicationContext()
const { getReferenceValues } = useMetadata()

const cancelReasons = computed(() => {
  const codes = application.value?.cancelReasons ?? []
  const values = getReferenceValues('cancelReasons')
  return codes.map((code) => values.find((v) => v.code === code)?.label ?? code)
})

const rejectReasons = computed(() => {
  const codes = application.value?.rejectReasons ?? []
  const values = getReferenceValues('rejectReasons')
  return codes.map((code) => values.find((v) => v.code === code)?.label ?? code)
})

const collateral = computed(() => application.value?.collateralProperty)

const showLoanAgreementsSection = computed(() => Boolean(application.value?.loanAgreements?.length))
const showDepositLoanAgreementsSection = computed(() => Boolean(application.value?.depositLoanAgreements?.length))
const showCashContributionSection = computed(() => application.value?.type === 'newLoanApplication')
const showMortgageDeedsSection = computed(
  () => isRealEstateCollateral(collateral.value) && !!collateral.value?.mortgageDeeds?.length,
)

const hasBridgeAndDepositLoansContent = computed(() => {
  const unsecuredLoansBreakdown = application.value?.unsecuredLoansBreakdown
  if (!unsecuredLoansBreakdown) return false

  return Boolean(
    unsecuredLoansBreakdown.bridgeLoanAmount ||
    unsecuredLoansBreakdown.bridgeLoanAmountAppliedFor ||
    unsecuredLoansBreakdown.depositLoan?.amount ||
    unsecuredLoansBreakdown.depositLoan?.amountAppliedFor ||
    unsecuredLoansBreakdown.propertySales.length,
  )
})

const shouldShowSection = (sectionKey: string) => {
  if (sectionKey === 'commitmentDetails') return false
  if (sectionKey === 'loanAgreements') return showLoanAgreementsSection.value
  if (sectionKey === 'depositLoanAgreements') return showDepositLoanAgreementsSection.value
  if (sectionKey === 'loan') return true
  if (sectionKey === 'cashContribution') return showCashContributionSection.value
  if (sectionKey === 'mortgageDeeds') return showMortgageDeedsSection.value
  return true
}

const shouldShowSubSection = (sectionKey: string, subSectionKey: string) => {
  if (sectionKey === 'cashContribution' && subSectionKey === 'bridgeAndDepositLoans') {
    return hasBridgeAndDepositLoansContent.value
  }

  if (sectionKey === 'loan' && subSectionKey === 'amortizationProof') {
    return application.value?.type === 'transferLoanApplication'
  }

  if (sectionKey === 'loan' && subSectionKey === 'topupPurpose') {
    return application.value?.type === 'topupLoanApplication' || application.value?.type === 'transferLoanApplication'
  }

  // Undersektionerna styrs av bostadstypen, inte av om datan finns: sektionen renderar en
  // egen text när lagfarten eller föreningen saknas, så navigeringen ska matcha den.
  if (sectionKey === 'property' && subSectionKey === 'titleDeed') {
    return isRealEstateCollateral(collateral.value)
  }

  if (sectionKey === 'property' && subSectionKey === 'housingAssociation') {
    return collateral.value?.type === 'apartment'
  }

  return true
}

const navigationSections = computed<NavigationSection[]>(() => {
  return Object.entries(applicationSections)
    .filter(([sectionKey]) => shouldShowSection(sectionKey))
    .map(([sectionKey, section]) => {
      const subSections = Object.entries(section.subSections ?? {})
        .filter(([subSectionKey]) => shouldShowSubSection(sectionKey, subSectionKey))
        .map(([, subSection]) => subSection)

      return {
        id: section.id,
        title: section.title,
        ...(subSections.length ? { subSections } : {}),
      }
    })
})
const sectionIds = computed(() => navigationSections.value.map((section) => section.id))
const ready = computed(() => !!application.value && !isLoading.value)
const { activeSection, lock } = useActiveSection(sectionIds, ready)

const scrollToCurrentHash = () => {
  const id = window.location.hash.replace(/^#/, '')
  if (!id) return

  const el = document.getElementById(id)
  if (el) {
    el.scrollIntoView()
  }
}

watch(ready, (isReady) => {
  if (isReady) {
    void nextTick(scrollToCurrentHash)
  }
})

onMounted(() => {
  window.addEventListener('popstate', scrollToCurrentHash)
})

onBeforeUnmount(() => {
  window.removeEventListener('popstate', scrollToCurrentHash)
})
</script>

<style lang="scss" scoped>
// Förmedlarens kommentar är fritext — behåll radbrytningar och bryt långa ord.
.broker-comment {
  white-space: pre-wrap;
  overflow-wrap: anywhere;
}

.application-cancelled-card {
  background-color: var(--color-danger-subtle);
  border: 1px solid var(--color-danger-emphasis);
  color: var(--color-danger-on-subtle);

  div {
    text-align: center;
    width: fit-content;
    margin: 0 auto;

    ul {
      text-align: left;
    }
  }
}
</style>
